import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { USER_ROLE } from '@/config/constants';
import {
  useAssignAgentToContractMutation,
  useGetAgentsByAdminQuery,
} from '@/apis/extended/admin/agentApi';
import { useGetUsersByAdminQuery } from '@/apis/extended/admin/userApi';
import { ManageAgentTable } from '@/components/Admin/ManageAgent/ManageAgentTable';
import { AddAgentModal } from '@/components/ConfirmModals/AddAgentModal';

export const AdminAgent = () => {
  const [isAddAgentModalOpen, setIsAddAgentModalOpen] = useState(false);
  const [contractId, setContractId] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [assignmentMessage, setAssignmentMessage] = useState<string | null>(
    null
  );

  const { data: agentsData } = useGetAgentsByAdminQuery({
    offset: 0,
    limit: 1000,
  });
  const { data: usersData } = useGetUsersByAdminQuery({
    offset: 0,
    limit: 1000,
  });
  const [assignAgentToContract, { isLoading: isAssigning }] =
    useAssignAgentToContractMutation();

  const agentOptions = useMemo(() => {
    return (agentsData?.agents || []).map((agent: any) => ({
      value: agent._id,
      label: agent.name || agent.email || agent._id,
    }));
  }, [agentsData]);

  const sellers = useMemo(() => {
    return (usersData?.users || []).filter(
      (user: any) => user.role === USER_ROLE.SELLER
    );
  }, [usersData]);

  const handleAssignAgent = async () => {
    if (!contractId || !selectedAgentId) {
      setAssignmentMessage('Please select an agent and contract ID.');
      return;
    }
    setAssignmentMessage(null);
    try {
      const result = await assignAgentToContract({
        agentId: selectedAgentId,
        contractId,
      }).unwrap();
      setAssignmentMessage(result?.message || 'Agent assigned successfully.');
      setContractId('');
      setSelectedAgentId('');
    } catch (error: any) {
      setAssignmentMessage(
        error?.data?.message || 'Failed to assign agent to contract.'
      );
    }
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 4,
          p: 3,
          borderRadius: '12px',
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Typography variant="h2" sx={{ mb: 2 }}>
          Assign Agent to Contract
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' },
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            select
            label="Agent"
            value={selectedAgentId}
            onChange={(e) => setSelectedAgentId(e.target.value)}
            fullWidth
          >
            {agentOptions.map((agent) => (
              <MenuItem key={agent.value} value={agent.value}>
                {agent.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Contract ID"
            value={contractId}
            onChange={(e) => setContractId(e.target.value)}
            placeholder="Paste contract ID"
            fullWidth
          />
          <Button
            variant="contained"
            onClick={handleAssignAgent}
            disabled={isAssigning}
            sx={{ height: 'fit-content' }}
          >
            Assign
          </Button>
        </Box>
        {assignmentMessage && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {assignmentMessage}
          </Alert>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: {
            xs: 'wrap',
            sm: 'nowrap',
          },
        }}
      >
        <Typography variant="h1">Agent Assignment</Typography>
        <Button
          sx={{ width: { xs: '100%', sm: 'unset' } }}
          endIcon={<i className="fa-light fa-plus" />}
          onClick={() => setIsAddAgentModalOpen(true)}
        >
          Add Agent
        </Button>
      </Box>
      <Box mt={3}>
        <ManageAgentTable />

        <Box sx={{ mt: 4 }}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            Sellers
          </Typography>
          {sellers.length === 0 ? (
            <Alert severity="info">No sellers found.</Alert>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Agent Email</TableCell>
                    <TableCell>Properties</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sellers.map((seller: any) => (
                    <TableRow key={seller._id}>
                      <TableCell>{seller.name || '—'}</TableCell>
                      <TableCell>{seller.email || '—'}</TableCell>
                      <TableCell>
                        {seller.latestSellerAgentEmail || '—'}
                      </TableCell>
                      <TableCell>{seller.propertyCount ?? 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
      <AddAgentModal
        isOpen={isAddAgentModalOpen}
        onClose={() => setIsAddAgentModalOpen(false)}
      />
    </Box>
  );
};
